// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ_DATA = [
  {
    category: 'Piattaforma e account',
    questions: [
      { q: 'SAPIENS è gratuito?', a: 'Sì. I livelli Base e Intermedio (8 lezioni), tutti i calcolatori tier 1, il glossario e la guida busta paga sono gratuiti per sempre. I livelli Avanzato ed Esperto, il Simulatore Portafoglio completo e il report PDF personalizzato richiedono il piano Premium (€4,99/mese nel periodo Fondatori).' },
      { q: 'Come creo un account?', a: 'Accedi con Google cliccando su "Accedi" in alto a destra. È sufficiente un account Google. Non è necessaria una registrazione separata.' },
      { q: 'Come funziona il sistema di progressione?', a: 'Completa tutte le lezioni di un livello per sbloccare il quiz. Supera il quiz (almeno 60%) per sbloccare il livello successivo. I progressi vengono salvati automaticamente nel tuo profilo.' },
      { q: 'Posso usare SAPIENS senza registrarmi?', a: 'Sì. Puoi vedere l\'elenco delle lezioni, usare i calcolatori e consultare il glossario senza account. Per tracciare i progressi, completare le lezioni e ottenere i certificati è necessario accedere.' },
    ],
  },
  {
    category: 'Calcolatori',
    questions: [
      { q: 'I risultati dei calcolatori sono accurati?', a: 'I calcolatori usano formule matematiche standard (es. formula dei mutui AMF, calcolo IRPEF con scaglioni aggiornati 2026, contributi INPS). I risultati sono indicativi e a scopo educativo. Per calcoli precisi con la tua situazione reale, consulta un professionista.' },
      { q: 'Posso salvare i miei calcoli?', a: 'Sì, con il piano Premium puoi salvare i tuoi calcoli nell\'archivio personale nel Dashboard, con accesso cronologico e possibilità di "rifarlo".' },
      { q: 'Quanti calcolatori ci sono?', a: 'SAPIENS include 25+ calcolatori in 7 categorie: Risparmio, Investimenti, Casa e Mutuo, Lavoro e Reddito, Previdenza, Protezione, Grandi Acquisti.' },
    ],
  },
  {
    category: 'Contenuti e lezioni',
    questions: [
      { q: 'Chi ha scritto i contenuti?', a: 'I contenuti sono scritti e revisionati dal team SAPIENS. Non siamo una banca, un broker o una compagnia assicurativa — siamo un progetto indipendente di educazione finanziaria.' },
      { q: 'I contenuti vengono aggiornati?', a: 'Sì. I calcolatori vengono aggiornati con le normative annuali (es. aliquote IRPEF, soglie INPS, parametri ISEE). Le lezioni vengono revisionate periodicamente.' },
      { q: 'Cosa sono i certificati?', a: 'Al completamento di ogni livello puoi scaricare un certificato PDF personalizzato con il tuo nome e un codice di verifica univoco. Il certificato ha valore educativo (non professionale) e documenta il tuo percorso.' },
    ],
  },
  {
    category: 'Premium',
    questions: [
      { q: 'Cosa include il piano Premium?', a: 'Premium include: Livelli Avanzato (L3) ed Esperto (L4), Simulatore Portafoglio completo con 56 strumenti e analisi scenari, archivio calcoli, report PDF personalizzato, e accesso prioritario alle nuove funzionalità.' },
      { q: 'Cos\'è il prezzo Fondatori?', a: 'Il prezzo Fondatori è €4,99/mese (o €39/anno) riservato ai primi iscritti fino al 31 agosto 2026. Chi si iscrive in questo periodo blocca questo prezzo. Dal 1° settembre il prezzo salirà a €6,99/mese nella Fase 2.' },
      { q: 'Posso cancellare in qualsiasi momento?', a: 'Sì. L\'abbonamento Premium è mensile o annuale, senza vincoli. Puoi cancellare in qualsiasi momento dal tuo profilo.' },
    ],
  },
  {
    category: 'Sicurezza e privacy',
    questions: [
      { q: 'SAPIENS vende i miei dati?', a: 'No. Non vendiamo dati a terze parti. Utilizziamo i tuoi dati solo per fornire il servizio (tracciare i progressi, personalizzare l\'esperienza). Leggi la nostra Privacy Policy per i dettagli.' },
      { q: 'Come viene protetto il mio account?', a: 'L\'autenticazione avviene tramite Google OAuth — non gestiamo password. Le sessioni sono protette con token sicuri. I dati sono archiviati su MongoDB in cloud.' },
      { q: 'SAPIENS è conforme al GDPR?', a: 'Sì. Puoi richiedere la cancellazione dei tuoi dati in qualsiasi momento scrivendo a privacy@finsapiens.it.' },
    ],
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
        data-testid={`faq-item-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="text-[#E8E8E4] text-sm font-medium leading-snug">{question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#C9A84C] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#888780] flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-sm text-[#888780] leading-relaxed pb-4">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>FAQ — Domande Frequenti SAPIENS</title>
        <meta name="description" content="Risposte alle domande più frequenti su SAPIENS: account, calcolatori, Premium, sicurezza e privacy." />
        <link rel="canonical" href="https://finsapiens.it/faq" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_DATA.flatMap(section => section.questions.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
          })))
        })}</script>
      </Helmet>

      <div className="container-wide py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="text-sm font-medium text-[#C9A84C] uppercase tracking-wider mb-3">FAQ</div>
          <h1 className="text-4xl font-semibold text-[#E8E8E4] mb-3">Domande frequenti</h1>
          <p className="text-[#888780] text-lg">Trova le risposte alle domande più comuni su SAPIENS.</p>
        </motion.div>

        {/* Quick nav */}
        <div className="flex flex-wrap gap-2 mb-10">
          {FAQ_DATA.map(section => (
            <button
              key={section.category}
              onClick={() => setActiveSection(activeSection === section.category ? null : section.category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeSection === section.category ? 'text-[#3A2800]' : 'text-[#888780] border border-white/10'}`}
              style={{ background: activeSection === section.category ? '#C9A84C' : '#252523' }}
            >
              {section.category}
            </button>
          ))}
        </div>

        {/* FAQ sections */}
        <div className="max-w-3xl space-y-8">
          {FAQ_DATA
            .filter(s => !activeSection || s.category === activeSection)
            .map((section, si) => (
              <motion.div key={section.category} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: si * 0.05 }}>
                <h2 className="text-base font-semibold text-[#C9A84C] mb-1 uppercase tracking-wider">{section.category}</h2>
                <div className="sapiens-card">
                  {section.questions.map((item, qi) => (
                    <FAQItem key={qi} question={item.q} answer={item.a} />
                  ))}
                </div>
              </motion.div>
            ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 p-8 rounded-2xl text-center max-w-xl" style={{ background: 'rgba(61,122,82,0.06)', border: '1px solid rgba(61,122,82,0.2)' }}>
          <h3 className="font-semibold text-[#E8E8E4] mb-2">Non hai trovato la risposta che cercavi?</h3>
          <p className="text-sm text-[#888780] mb-4">Scrivici a <strong className="text-[#E8E8E4]">info@finsapiens.it</strong> — rispondiamo entro 24 ore nei giorni lavorativi.</p>
          <Link to="/percorsi" className="sapiens-btn-primary px-6 py-2.5 inline-block text-sm">
            Inizia il percorso gratuito
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
